import { Injectable } from '@angular/core';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { SearchGroupsAction } from './search-groups.actions';
import { GroundFoundDto, GroupsService } from '@smatchy/sdk';
import { FilterEventsSelector } from '@smatchy/core';
import { buildLocationSearchFields } from '../../../utils/build-location-search-fields.util';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ShowToastErrorAction } from '../../app';

export interface SearchGroupsStateModel {
  groups: GroundFoundDto[];
}

const defaults: SearchGroupsStateModel = {
  groups: [],
};

@State<SearchGroupsStateModel>({
  name: 'searchGroups',
  defaults,
})
@Injectable()
export class SearchGroupsState {
  constructor(
    private readonly _groupsService: GroupsService,
    private readonly _store: Store,
    private readonly _translate: TranslateService,
  ) {}

  @Action(SearchGroupsAction)
  async searchGroupsAction({ patchState }: StateContext<SearchGroupsStateModel>, { payload }: SearchGroupsAction) {
    const filter = this._store.selectSnapshot(FilterEventsSelector.filter);
    const locationFields = buildLocationSearchFields(filter);
    return this._groupsService
      .searchGroups({
        findGroupsDto: {
          ...locationFields,
          sportIds: payload.sportsSelected || filter.sportsSelected || [],
        },
      })
      .subscribe({
        next: (res) => {
          if (!carriesAValue(res)) {
            console.error(res.error);
            return;
          }
          const normalized = (res.value.groups || []).map((g) => ({
            ...g,
            subGroupCount: g.subGroupsCount ?? 0,
            totalMembersCount: g.totalMembersIncludingSubgroups ?? 0,
          }));

          patchState({
            groups: normalized as GroundFoundDto[],
          });
        },
        // Same endpoint as the explore search (CAPACITOR-18). Without an error
        // callback a failed call surfaced as an unhandled error: a network drop
        // (status 0) now stays silent, any other failure says the search failed.
        error: (error: unknown) => {
          const isNetworkDrop = error instanceof HttpErrorResponse && error.status === 0;
          if (!isNetworkDrop) {
            this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GROUPS.ERRORS.SEARCH_FAILED')));
          }
        },
      });
  }
}
